// package imports
import { Theme as MuiTheme } from '@mui/material/styles';
import { ElementType, ReactNode, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';

// local imports
import { headingColor } from 'services/styleUtils/headingStyleUtils';
import { MegaMenuCTACard } from '../SiteHeaderTypes';
import { KitButton } from 'components/common/cms-kit';
import { Freeform } from 'components/common/Freeform';
import { parseBlocks } from 'services/parseUtils';
import { buttonColor } from 'services/styleUtils/buttonStyleUtils';
import { LessonPlan } from 'types/AgilityTypes';
import { COMMON_ROUTES } from 'services/constants';

const DynamicTag = ({
  tag,
  children,
  ...props
}: {
  tag: ElementType | string;
  children: ReactNode;
  [key: string]: any;
}) => {
  const Tag = tag;
  return <Tag {...props}>{children}</Tag>;
};

const MegaMenuCtaCard = ({
  cardData,
  lessonPlanOfTheWeek,
}: {
  cardData: MegaMenuCTACard;
  lessonPlanOfTheWeek?: LessonPlan;
}) => {
  const theme = useTheme() as MuiTheme;

  const button = Array.isArray(cardData.fields?.button)
    ? cardData.fields?.button[0]
    : undefined;
  const isLessonPlanOfTheWeek =
    cardData.fields?.isLessonPlanOfTheWeek === 'true';
  const href =
    isLessonPlanOfTheWeek && lessonPlanOfTheWeek
      ? `${COMMON_ROUTES.LessonPlan}/${lessonPlanOfTheWeek?.fields?.slug}`
      : button?.fields?.link?.href;
  const backgroundImageStyle = useMemo(
    () => ({
      backgroundImage: `linear-gradient(to top, rgba(0, 85, 128, 1), rgba(0, 82, 124, 0.70), rgba(2, 73, 110, 0.30), rgba(7, 30, 49, 0)), url(${cardData.fields?.backgroundImage?.url})`,
    }),
    [cardData.fields?.backgroundImage?.url],
  );

  if (!cardData.fields) {
    return null;
  }

  return (
    <div
      className="rounded-xl bg-cover bg-no-repeat relative min-h-[240px] relative"
      style={backgroundImageStyle}
    >
      <div className="absolute bottom-0">
        <div className="flex flex-col content-start flex-wrap p-4 gap-y-2">
          <DynamicTag
            tag={cardData.fields?.headingLevel || 'h4'}
            id="header"
            style={{
              color: headingColor(
                theme,
                cardData.fields?.headingStyle?.fields?.color,
              ),
              fontSize: '24px',
              fontWeight: 'semibold',
            }}
          >
            {cardData.fields?.headingText}
          </DynamicTag>
          <div
            id="content"
            style={{
              color: cardData.fields?.contentStyle?.fields?.textColor,
              fontSize: cardData.fields?.contentStyle?.fields?.textSize,
            }}
          >
            {isLessonPlanOfTheWeek && lessonPlanOfTheWeek ? (
              <span>{lessonPlanOfTheWeek.fields?.title}</span>
            ) : (
              <Freeform blocks={parseBlocks(cardData.fields?.contentText)} />
            )}
          </div>
          {button && (
            <div className="m-x-auto">
              <KitButton
                round
                size="sm"
                style={{
                  border:
                    button.fields?.style?.fields?.variant === 'outlined'
                      ? `1px solid ${buttonColor(theme, button.fields?.style?.fields?.color)}`
                      : 'none',
                  color: buttonColor(
                    theme,
                    button.fields?.style?.fields?.color,
                  ),
                  backgroundColor:
                    button.fields?.style?.fields?.variant === 'outlined'
                      ? 'transparent'
                      : buttonColor(theme, button.fields?.style?.fields?.color),
                }}
                href={href}
                target={button.fields?.link?.target}
                rel={`noopener noreferrer ${button.fields.addNoFollowtoUrlLinkField === 'true' ? 'nofollow' : ''}`}
              >
                {button.fields?.label}
              </KitButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MegaMenuCtaCard;
